import React, { useState, useEffect, useRef, useCallback } from 'react';

import QueryChat from './QueryChat';
// eslint-disable-next-line no-unused-vars
import TestCasesTable from './TestCasesTable';
import TestCasesManager from './TestCasesManager';
import ErrorPage from './ErrorPage';
import aiLogger from '../utils/aiLogger';
import StratovaiLoader from './StratovaiLoader';
import ModernNavigation from './ModernNavigation';
import ProductKnowledge from './ProductKnowledge';
import ModernDashboard from './ModernDashboard';
import QualityDashboard from './quality/QualityDashboard';
import HealthInsights from './health/HealthInsights';
import AutomationCases from './AutomationCases';
import SafeSummaryEligibilityAdmin from './SafeSummaryEligibilityAdmin';
import RepositorySourceConfigAdmin from './RepositorySourceConfigAdmin';
import CostBreakerBanner from './CostBreakerBanner';
import { PKB_OPEN_SOURCE_REF_STORAGE_KEY } from './ProductKnowledge/productKnowledgeUtils';

const Dashboard = ({ 
  authenticatedUser, 
  selectedProject, 
  loginConfig, 
  onLogout,
  onBackToProjectSelection,
  onProjectSwitch,
  onProjectReconcile,
  onNavigateToUserManager,
  onNavigateToSettings,
  onOpenChangePassword,
  apiClient
}) => {
  const isAdmin = authenticatedUser?.role === 'project_admin' || authenticatedUser?.role === 'super_admin';
  const ALLOWED_TABS = new Set([
    'overview',
    'quality',
    'test-cases',
    'chat',
    'product-knowledge',
    'health-insights',
    'automation',
  ]);
  if (isAdmin) {
    ALLOWED_TABS.add('safe-summary-eligibility');
    ALLOWED_TABS.add('repository-source-config');
  }
  const coerceAllowedTab = (tabName) => (ALLOWED_TABS.has(tabName) ? tabName : 'overview');
  const [projectSummary, setProjectSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    // Try to load active tab from localStorage on first render
    const stored = localStorage.getItem('dashboard_active_tab');
    return coerceAllowedTab(stored || 'overview');
  });
  const [explorationStatus, setExplorationStatus] = useState(null);
  const [explorationResults, setExplorationResults] = useState(null);
  const pollingRef = useRef(null);
  const [testCases, setTestCases] = useState([]); // NEW: test cases from API
  const [uiExplorerError, setUIExplorerError] = useState(null);
  const [automationResults, setAutomationResults] = useState(null);
  const previousProjectIdRef = useRef(selectedProject?.project_id);

  useEffect(() => {
    const currentProjectId = selectedProject?.project_id;
    const previousProjectId = previousProjectIdRef.current;

    if (
      previousProjectId != null &&
      currentProjectId != null &&
      String(previousProjectId) !== String(currentProjectId)
    ) {
      setActiveTab('overview');
      localStorage.setItem('dashboard_active_tab', 'overview');
      localStorage.removeItem(PKB_OPEN_SOURCE_REF_STORAGE_KEY);
      sessionStorage.removeItem('test_cases_latest_run_id');
      setProjectSummary(null);
      setTestCases([]);
      setExplorationStatus(null);
      setExplorationResults(null);
      setAutomationResults(null);
      setUIExplorerError(null);
    }

    previousProjectIdRef.current = currentProjectId;
  }, [selectedProject?.project_id]);

  // Tab switching handler
  const handleTabChange = useCallback((tabName) => {
    const safeTab = coerceAllowedTab(tabName);
    const previousTab = activeTab;
    setActiveTab(safeTab);
    
    // Save active tab to localStorage
    localStorage.setItem('dashboard_active_tab', safeTab);
    
    // Log user interaction (aiLogger is stable, no need for dependency)
    aiLogger.userInteraction('tab_change', null, {
      previousTab,
      newTab: safeTab,
      projectId: selectedProject?.project_id
    });
  }, [activeTab, selectedProject?.project_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProjectSummary = useCallback(async () => {
    if (!selectedProject?.project_id) return;
    
    try {
      const summary = await apiClient.getProjectSummary(selectedProject.project_id);
      setProjectSummary(summary);
      aiLogger.debug('Project Summary API Response:', summary);
      aiLogger.debug('Statistics:', summary?.statistics);
    } catch (error) {
      aiLogger.error('Failed to fetch project summary', error);
    }
  }, [selectedProject?.project_id, apiClient]);

  const fetchTestCases = useCallback(async () => {
    if (!selectedProject) return;
    try {
      const response = await apiClient.getProjectTestCases(selectedProject.project_id);
      // Support both { test_cases: [...] } and direct array
      const cases = Array.isArray(response)
        ? response
        : (response.test_cases || []);
      setTestCases(cases);
    } catch (error) {
      setTestCases([]);
      aiLogger.warn('Failed to fetch test cases', error);
    }
  }, [selectedProject, apiClient]);

  const fetchExplorationResults = useCallback(async () => {
    try {
      const results = await apiClient.getExplorationResults(selectedProject.project_id);
      setExplorationResults(results);
    } catch (error) {
      aiLogger.error('Error fetching exploration results:', error);
    }
  }, [selectedProject.project_id, apiClient]);

  // Poll backend for status
  const pollExplorationStatus = useCallback(async () => {
    if (!authenticatedUser?.user_id || !selectedProject?.project_id) {
      aiLogger.debug('Missing user_id or project_id for polling');
      return;
    }

    try {
      const status = await apiClient.getExplorationStatus(authenticatedUser.user_id, selectedProject.project_id);
      aiLogger.debug('Polling exploration status:', status);
      
      if (status && status.status) {
        setExplorationStatus(status);
        const key = `explorationStatus_${authenticatedUser.user_id}_${selectedProject.project_id}`;
        localStorage.setItem(key, JSON.stringify(status));
        if (["completed", "failed", "not_started"].includes(status.status)) {
          clearInterval(pollingRef.current);
          if (status.status === "completed") {
            // Enhanced refresh logic with multiple attempts
            aiLogger.success('UI Exploration completed, refreshing data...');
            
            // Force refresh all data with retries
            const refreshData = async () => {
              try {
                // Refresh exploration results first
                await fetchExplorationResults();
                await new Promise(resolve => setTimeout(resolve, 1000));
                
                // Refresh project summary
                await fetchProjectSummary();
                await new Promise(resolve => setTimeout(resolve, 1000));
                
                // Refresh test cases with retry
                const maxRetries = 3;
                let currentRetry = 0;
                while (currentRetry < maxRetries) {
                  try {
                    await fetchTestCases();
                    aiLogger.success('Test cases refreshed successfully');
                    break;
                  } catch (error) {
                    currentRetry++;
                    const retryDelay = 2000 * currentRetry;
                    aiLogger.warn(`Test cases refresh attempt ${currentRetry} failed:`, error);
                    if (currentRetry < maxRetries) {
                      await new Promise(resolve => setTimeout(resolve, retryDelay));
                    }
                  }
                }
                
                // Force UI update by triggering a re-render
                setTestCases(prev => [...prev]);
                
              } catch (error) {
                aiLogger.error('Error during data refresh after exploration:', error);
              }
            };
            
            refreshData();
          }
        }
      }
    } catch (error) {
      aiLogger.error('Error polling exploration status:', error);
      // Retry with exponential backoff
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        setTimeout(() => {
          pollExplorationStatus();
        }, 5000); // Wait 5 seconds before retrying
      }
    }
  }, [authenticatedUser?.user_id, selectedProject?.project_id, apiClient, fetchExplorationResults, fetchProjectSummary, fetchTestCases]);



  // Fix useEffect dependencies
  // 1. useEffect for fetchProjectSummary and fetchTestCases
  useEffect(() => {
    if (selectedProject && authenticatedUser?.user_id) {
      // Avoid dashboard/test-case fetches when in Automation tab
      if (activeTab !== 'automation-test-cases') {
        fetchProjectSummary();
        fetchTestCases();
      }
      setLoading(false);
    }
  }, [selectedProject, authenticatedUser?.user_id, activeTab, fetchProjectSummary, fetchTestCases]);

  // 2. useEffect for exploration status and results
  useEffect(() => {
    if (authenticatedUser && selectedProject) {
      const key = `explorationStatus_${authenticatedUser.user_id}_${selectedProject.project_id}`;
      const stored = localStorage.getItem(key);
      if (stored) {
        setExplorationStatus(JSON.parse(stored));
        // Only poll if in progress
        const parsed = JSON.parse(stored);
        if (parsed.status === 'in_progress') {
          // Start polling with interval
          if (pollingRef.current) clearInterval(pollingRef.current);
          pollExplorationStatus(); // Initial call
          pollingRef.current = setInterval(pollExplorationStatus, 10000); // Poll every 10 seconds
        }
      }
      // Only fetch exploration results if we're in a tab that needs them
      if (activeTab === 'ui-explorer' || activeTab === 'overview') {
        fetchExplorationResults();
      }
    }
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [authenticatedUser, selectedProject, activeTab, fetchExplorationResults, pollExplorationStatus]);

  // Fetch exploration results when switching to tabs that need them
  useEffect(() => {
    if (authenticatedUser && selectedProject && (activeTab === 'ui-explorer' || activeTab === 'overview')) {
      fetchExplorationResults();
    }
  }, [activeTab, authenticatedUser, selectedProject, fetchExplorationResults]);

  // 1. Add useEffect to trigger automation generation after UI Exploration completes
  const [automationTriggered, setAutomationTriggered] = useState(false);
  
  useEffect(() => {
    if (!ALLOWED_TABS.has(activeTab)) {
      setActiveTab('overview');
      localStorage.setItem('dashboard_active_tab', 'overview');
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (explorationStatus && explorationStatus.status === 'completed' && !automationTriggered) {
      setAutomationTriggered(true);
      (async () => {
        setExplorationStatus((prev) => ({ ...prev, message: 'Generating automation scripts...' }));
        try {
          // Automation generation remains explicitly disabled in Phase 0.
          setExplorationStatus((prev) => ({
            ...prev,
            message: 'Automation generation is disabled in current phase.',
          }));
        } catch (e) {
          aiLogger.error('Automation generation failed:', e);
          setExplorationStatus((prev) => ({ ...prev, message: 'Automation generation failed.' }));
        }
      })();
    }
  }, [explorationStatus, selectedProject?.project_id, authenticatedUser?.user_id, apiClient, fetchProjectSummary, fetchTestCases, automationTriggered, explorationResults]);

  // 2. Add state and handler for running automation
  const [automationRunResult, setAutomationRunResult] = useState(null);
  
  const handleRunAutomation = async () => {
    setAutomationRunResult('Automation run is disabled in current phase.');
    return;
    
    // Cycle through messages
  };

  const handleGenerateAutomation = async () => {
    setAutomationResults({
      success: false,
      message: 'Automation generation is disabled in current phase.',
    });
    alert('Automation generation is disabled in current phase.');
  };

  // 3. useEffect for navigation events
  useEffect(() => {
    const handleNavigateToTab = (event) => {
      const { tab } = event.detail;
      if (tab === 'testCases') {
        handleTabChange('test-cases');
      } else if (tab === 'productKnowledge') {
        handleTabChange('product-knowledge');
      }
    };
    const handleNavigateToProductKnowledge = (event) => {
      const sourceRef = event?.detail?.sourceRef;
      if (sourceRef) {
        try {
          localStorage.setItem(PKB_OPEN_SOURCE_REF_STORAGE_KEY, JSON.stringify(sourceRef));
        } catch {}
      }
      handleTabChange('product-knowledge');
    };
    window.addEventListener('navigateToTab', handleNavigateToTab);
    window.addEventListener('navigateToProductKnowledge', handleNavigateToProductKnowledge);
    return () => {
      window.removeEventListener('navigateToTab', handleNavigateToTab);
      window.removeEventListener('navigateToProductKnowledge', handleNavigateToProductKnowledge);
    };
  }, [handleTabChange]);

  // 4. Refresh data when switching to overview tab
  useEffect(() => {
    if (activeTab === 'overview' && selectedProject && authenticatedUser) {
      fetchProjectSummary();
      fetchTestCases();
    }
  }, [activeTab, selectedProject, authenticatedUser, fetchProjectSummary, fetchTestCases]);

  const handleStartExploration = async () => {
    if (!selectedProject || !authenticatedUser) {
      aiLogger.error('Missing project or user data');
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm(
      '🚀 Universal Learning Explorer will generate intelligent test cases using AI-powered learning!\n\n' +
      'This advanced system combines template-based patterns with LLM-powered analysis for comprehensive website exploration.\n\n' +
      'Are you sure you want to start the Universal Learning Explorer?'
    );

    if (!confirmed) {
      aiLogger.debug('Universal Learning Explorer cancelled by user');
      return;
    }

    aiLogger.debug('🚀 Starting Universal Learning Explorer with:', {
      selectedProject,
      authenticatedUser,
      project_id: selectedProject.project_id,
      user_id: authenticatedUser.user_id
    });

    try {
      setAutomationTriggered(false); // Reset automation trigger flag
      setExplorationStatus({
        status: 'in_progress',
        message: 'Starting Universal Learning Explorer...'
      });

      // Start Universal Learning Explorer
      const response = await apiClient.startUniversalExploration(
        authenticatedUser.user_id,
        selectedProject.project_id,
        "config"
      );

      aiLogger.success('✅ Universal Learning Explorer API response:', response);

      if (response.status === 'in_progress' && response.session_id) {
        setExplorationStatus({
          status: 'in_progress',
          message: 'Universal Learning Explorer is running...',
          sessionId: response.session_id
        });
        
        // Start polling with interval
        if (pollingRef.current) clearInterval(pollingRef.current);
        pollUniversalExplorationStatus(response.session_id); // Initial call
        pollingRef.current = setInterval(() => pollUniversalExplorationStatus(response.session_id), 10000); // Poll every 10 seconds
      } else {
        setExplorationStatus({
          status: 'failed',
          message: response.message || 'Failed to start Universal Learning Explorer'
        });
      }
    } catch (error) {
      aiLogger.error('❌ Universal Learning Explorer error:', error);
      setExplorationStatus({
        status: 'failed',
        message: error.message || 'Universal Learning Explorer failed'
      });
    }
  };

  // Poll Universal Learning Explorer status
  const pollUniversalExplorationStatus = async (sessionId) => {
    try {
      aiLogger.debug('🔄 Polling Universal Learning Explorer status for session:', sessionId);
      
      const statusResponse = await apiClient.getUniversalExplorationStatus(sessionId);
      
      aiLogger.debug('📊 Universal Learning Explorer status:', statusResponse);
      
      if (statusResponse.status === 'completed') {
        // Exploration completed successfully
        setExplorationStatus({
          status: 'completed',
          message: 'Universal Learning Explorer completed successfully!',
          sessionId: sessionId
        });
        
        // Get results
        const resultsResponse = await apiClient.getUniversalExplorationResults(sessionId);
        aiLogger.success('🎉 Universal Learning Explorer results:', resultsResponse);
        
        // Update exploration results
        setExplorationResults(resultsResponse);
        
        // Clear polling
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        
        // Refresh project data
        fetchProjectSummary();
        fetchTestCases();
        
      } else if (statusResponse.status === 'failed') {
        // Exploration failed
        setExplorationStatus({
          status: 'failed',
          message: statusResponse.message || 'Universal Learning Explorer failed',
          sessionId: sessionId
        });
        
        // Clear polling
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        
      } else if (statusResponse.status === 'in_progress') {
        // Still in progress, update message
        setExplorationStatus({
          status: 'in_progress',
          message: statusResponse.message || 'Universal Learning Explorer is running...',
          sessionId: sessionId
        });
      }
      
    } catch (error) {
      aiLogger.error('❌ Error polling Universal Learning Explorer status:', error);
      setExplorationStatus({
        status: 'failed',
        message: 'Failed to get exploration status: ' + error.message,
        sessionId: sessionId
      });
      
      // Clear polling on error
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleStartAIChat = async () => {
    try {
      const result = await apiClient.makeRequest('/orchestrator/ai-chat', {
        method: 'POST',
        body: JSON.stringify({
          user_id: authenticatedUser.user_id,
          project_id: selectedProject.project_id,
          query: "What are the main features of this platform?"
        })
      });
      aiLogger.debug('AI Chat response:', result);
      // TODO: Show chat interface
    } catch (error) {
      aiLogger.error('Error starting AI chat:', error);
    }
  };

  if (loading) {
    return (
      <StratovaiLoader />
    );
  }

  // Helper to show persistent status message
  // eslint-disable-next-line no-unused-vars
  const renderExplorationStatus = () => {
    if (explorationStatus && explorationStatus.status === 'in_progress') {
      return (
        <div className="exploration-status-message" style={{
          background: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: '8px',
          padding: '16px',
          margin: '16px 0',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{ fontSize: '24px' }}>🚀</div>
          <div>
            <div style={{ fontWeight: 'bold', color: '#92400e', marginBottom: '4px' }}>
              🔍 Universal Learning Explorer in Progress
            </div>
            <div style={{ color: '#92400e', fontSize: '14px' }}>
              {explorationStatus.message || 'Universal Learning Explorer is running...'}
            </div>
          </div>
        </div>
      );
    } else if (explorationStatus && explorationStatus.status === 'completed') {
      return (
        <div className="exploration-status-message" style={{
          background: '#d1fae5',
          border: '1px solid #10b981',
          borderRadius: '8px',
          padding: '16px',
          margin: '16px 0',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{ fontSize: '24px' }}>🎉</div>
          <div>
            <div style={{ fontWeight: 'bold', color: '#065f46', marginBottom: '4px' }}>
              ✅ Universal Learning Explorer completed successfully
            </div>
            <div style={{ color: '#065f46', fontSize: '14px' }}>
              Intelligent test cases have been generated using AI-powered learning!
            </div>
          </div>
        </div>
      );
    } else if (explorationStatus && explorationStatus.status === 'failed') {
      return (
        <div className="exploration-status-message" style={{
          background: '#fee2e2',
          border: '1px solid #ef4444',
          borderRadius: '8px',
          padding: '16px',
          margin: '16px 0',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{ fontSize: '24px' }}>❌</div>
          <div>
            <div style={{ fontWeight: 'bold', color: '#991b1b', marginBottom: '4px' }}>
              ❌ Universal Learning Explorer Failed
            </div>
            <div style={{ color: '#991b1b', fontSize: '14px' }}>
              {explorationStatus.message || 'Universal Learning Explorer encountered an error'}
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Dismiss error handler
  // eslint-disable-next-line no-unused-vars
  const handleDismissExplorationError = () => {
    setExplorationStatus(null);
    if (authenticatedUser && selectedProject) {
      const key = `explorationStatus_${authenticatedUser.user_id}_${selectedProject.project_id}`;
      localStorage.removeItem(key);
    }
  };

  // Helper to render exploration results
  // eslint-disable-next-line no-unused-vars
  const renderExplorationResults = () => {
    if (!explorationResults || Object.keys(explorationResults).length === 0) {
      return null;
    }

    return (
      <div className="exploration-results" style={{marginTop: '2rem'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
          <h3 style={{color: '#2e2e2e', margin: 0}}>🎯 Universal Learning Explorer Results</h3>
          <button 
            onClick={() => {
              fetchExplorationResults();
              fetchProjectSummary();
              fetchTestCases();
            }}
            style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '0.5rem 1rem',
              fontSize: '0.9rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            🔄 Refresh Results
          </button>
        </div>

        {/* Summary Statistics Cards */}
        <div className="fade-in" style={{display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: '2rem'}}>
          <div style={{
            backgroundColor: '#dbeafe',
            border: '1px solid #93c5fd',
            borderRadius: '12px',
            padding: '1.5rem',
            textAlign: 'center'
          }}>
            <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#1e40af', marginBottom: '0.5rem'}}>
              {uiFeaturesCount}
            </div>
            <div style={{color: '#1e40af', fontWeight: '600'}}>Features Discovered</div>
          </div>
          
          <div style={{
            backgroundColor: '#fef3c7',
            border: '1px solid #fcd34d',
            borderRadius: '12px',
            padding: '1.5rem',
            textAlign: 'center'
          }}>
            <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#92400e', marginBottom: '0.5rem'}}>
              {automationScriptsCount}
            </div>
            <div style={{color: '#92400e', fontWeight: '600'}}>Automation Scripts</div>
          </div>
          
          <div style={{
            backgroundColor: '#d1fae5',
            border: '1px solid #86efac',
            borderRadius: '12px',
            padding: '1.5rem',
            textAlign: 'center'
          }}>
            <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#065f46', marginBottom: '0.5rem'}}>
              {userFlowsCount}
            </div>
            <div style={{color: '#065f46', fontWeight: '600'}}>User Flows</div>
          </div>
          
          <div style={{
            backgroundColor: '#f3e8ff',
            border: '1px solid #c4b5fd',
            borderRadius: '12px',
            padding: '1.5rem',
            textAlign: 'center'
          }}>
            <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#7c3aed', marginBottom: '0.5rem'}}>
              {testScenariosCount}
            </div>
            <div style={{color: '#7c3aed', fontWeight: '600'}}>Test Cases</div>
          </div>
        </div>
        
        {/* Discovered Features */}
        <div className="card" style={{marginBottom: '1.5rem'}}>
          <h4 style={{color: '#2e2e2e', marginBottom: '1rem'}}>🔍 Discovered Features</h4>
          <div style={{display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))'}}>
            {explorationResults.discovered_features?.map((feature, index) => (
              <div key={index} style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '1rem',
                backgroundColor: '#f9fafb'
              }}>
                <h5 style={{color: '#2e2e2e', margin: '0 0 0.5rem 0', fontSize: '1rem'}}>{feature.name}</h5>
                <p style={{color: '#6b7280', margin: '0 0 0.5rem 0', fontSize: '0.9rem'}}>{feature.description}</p>
                <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap'}}>
                  {feature.elements?.map((element, elemIndex) => (
                    <span key={elemIndex} style={{
                      backgroundColor: '#e5e7eb',
                      color: '#374151',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.8rem'
                    }}>
                      {element}
                    </span>
                  ))}
                </div>
                <div style={{
                  marginTop: '0.5rem',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  backgroundColor: feature.priority === 'high' ? '#fef3c7' : '#dbeafe',
                  color: feature.priority === 'high' ? '#92400e' : '#1e40af'
                }}>
                  {feature.priority} priority
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Discovered Elements */}
        <div className="card" style={{marginBottom: '1.5rem'}}>
          <h4 style={{color: '#2e2e2e', marginBottom: '1rem'}}>🎯 Discovered UI Elements</h4>
          <div style={{display: 'grid', gap: '0.5rem', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))'}}>
            {explorationResults.discovered_elements?.map((element, index) => (
              <div key={index} style={{
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '0.75rem',
                backgroundColor: '#ffffff',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <div style={{fontWeight: '600', color: '#2e2e2e', fontSize: '0.9rem'}}>{element.text}</div>
                  <div style={{color: '#6b7280', fontSize: '0.8rem', fontFamily: 'monospace'}}>{element.selector}</div>
                </div>
                <div style={{
                  backgroundColor: element.action === 'click' ? '#dbeafe' : element.action === 'type' ? '#fef3c7' : '#d1fae5',
                  color: element.action === 'click' ? '#1e40af' : element.action === 'type' ? '#92400e' : '#065f46',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.8rem',
                  fontWeight: '600'
                }}>
                  {element.action}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Test Cases in Human-Readable Format */}
        {explorationResults.test_scenarios && explorationResults.test_scenarios.length > 0 && (
          <div className="card" style={{marginBottom: '1.5rem'}}>
            <h4 style={{color: '#2e2e2e', marginBottom: '1rem'}}>🧪 Generated Test Cases</h4>
            <div style={{display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))'}}>
              {explorationResults.test_scenarios.map((scenario, index) => (
                <div key={index} style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '1.5rem',
                  backgroundColor: '#f9fafb'
                }}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem'}}>
                    <h5 style={{color: '#2e2e2e', margin: 0, fontSize: '1.1rem'}}>{scenario.name}</h5>
                    <div style={{
                      backgroundColor: scenario.priority === 'high' ? '#fef3c7' : scenario.priority === 'medium' ? '#dbeafe' : '#f3e8ff',
                      color: scenario.priority === 'high' ? '#92400e' : scenario.priority === 'medium' ? '#1e40af' : '#7c3aed',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      fontWeight: '600'
                    }}>
                      {scenario.priority} priority
                    </div>
                  </div>
                  <p style={{color: '#6b7280', margin: '0 0 1rem 0', fontSize: '0.9rem', lineHeight: '1.5'}}>
                    {scenario.description}
                  </p>
                  <div style={{marginBottom: '1rem'}}>
                    <div style={{fontWeight: '600', color: '#2e2e2e', marginBottom: '0.5rem'}}>Test Steps:</div>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
                      {scenario.steps?.map((step, stepIndex) => (
                        <div key={stepIndex} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.75rem',
                          backgroundColor: '#ffffff',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb'
                        }}>
                          <span style={{
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.8rem',
                            fontWeight: '600'
                          }}>
                            {stepIndex + 1}
                          </span>
                          <div style={{flex: 1}}>
                            <div style={{fontWeight: '600', color: '#2e2e2e', fontSize: '0.9rem'}}>
                              {step.action.charAt(0).toUpperCase() + step.action.slice(1)} {step.target}
                            </div>
                            <div style={{color: '#6b7280', fontSize: '0.8rem', marginTop: '0.25rem'}}>
                              Expected: {step.expected}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {scenario.tags && scenario.tags.length > 0 && (
                    <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap'}}>
                      {scenario.tags.map((tag, tagIndex) => (
                        <span key={tagIndex} style={{
                          backgroundColor: '#e5e7eb',
                          color: '#374151',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.8rem'
                        }}>
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* User Flows */}
        {explorationResults.user_flows && explorationResults.user_flows.length > 0 && (
          <div className="card">
            <h4 style={{color: '#2e2e2e', marginBottom: '1rem'}}>🔄 User Flows</h4>
            <div style={{display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))'}}>
              {explorationResults.user_flows.map((flow, index) => (
                <div key={index} style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '1rem',
                  backgroundColor: '#f9fafb'
                }}>
                  <h5 style={{color: '#2e2e2e', margin: '0 0 0.75rem 0', fontSize: '1rem'}}>{flow.name}</h5>
                  <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
                    {flow.steps?.map((step, stepIndex) => (
                      <div key={stepIndex} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem',
                        backgroundColor: '#ffffff',
                        borderRadius: '4px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <span style={{
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.7rem',
                          fontWeight: '600'
                        }}>
                          {stepIndex + 1}
                        </span>
                        <div style={{flex: 1}}>
                          <div style={{fontWeight: '600', color: '#2e2e2e', fontSize: '0.9rem'}}>{step.action} {step.target}</div>
                          <div style={{color: '#6b7280', fontSize: '0.8rem'}}>Expected: {step.expected}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Automation Scripts */}
        <div className="card" style={{marginBottom: '1.5rem'}}>
          <h4 style={{color: '#2e2e2e', marginBottom: '1rem'}}>⚙️ Automation Scripts</h4>
          <p style={{color: '#6b7280', lineHeight: '1.6', marginBottom: '1.5rem', fontSize: '0.9rem'}}>
            Review and manage generated automation scripts. You can run them to verify functionality.
          </p>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
            <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#92400e', marginBottom: '0.5rem'}}>
              {automationScriptsCount}
            </div>
            <div style={{display: 'flex', gap: '0.5rem'}}>
              <button onClick={handleGenerateAutomation} className="button" style={{marginTop: '1rem'}}>
                Generate Automation
              </button>
              <button onClick={handleRunAutomation} className="button" style={{marginTop: '1rem'}} disabled={automationScriptsCount === 0}>
                {automationScriptsCount === 0 ? 'No Scripts' : 'Run Automation'}
              </button>
            </div>
          </div>
          {automationRunResult && <div style={{marginTop: '0.5rem', color: '#065f46'}}>{automationRunResult}</div>}
        </div>
      </div>
    );
  };

  // Extract test cases by tag for filtering
  // eslint-disable-next-line no-unused-vars
  const extractByTag = (tag) => testCases.filter(tc => tc.tags && tc.tags.includes(tag));

  // Use statistics from API response (single source of truth)
  const uiFeaturesCount = projectSummary?.statistics?.total_features_discovered || 0;
  const userFlowsCount = projectSummary?.statistics?.total_user_flows || 0;
  const testScenariosCount = projectSummary?.statistics?.total_test_cases || 0;
  
  // Calculate automation scripts count from statistics
  const automationScriptsCount = projectSummary?.statistics?.total_automation_scripts || 0;
  
  // Additional statistics
  const explorationSessionsCount = projectSummary?.statistics?.total_exploration_sessions || 0;
  const successfulExplorationsCount = projectSummary?.statistics?.successful_exploration_sessions || 0;
  const approvedTestCasesCount = projectSummary?.statistics?.approved_test_cases || 0;
  const pendingTestCasesCount = projectSummary?.statistics?.pending_test_cases || 0;
  
  const coveragePercent = projectSummary?.statistics?.coverage_percentage || 0;
  
  // Debug logging for statistics
  aiLogger.debug('UI Features Count:', uiFeaturesCount);
  aiLogger.debug('User Flows Count:', userFlowsCount);
  aiLogger.debug('Test Scenarios Count:', testScenariosCount);
  aiLogger.debug('Automation Scripts Count:', automationScriptsCount);
  aiLogger.debug('Exploration Sessions Count:', explorationSessionsCount);
  aiLogger.debug('Successful Explorations Count:', successfulExplorationsCount);
  aiLogger.debug('Approved Test Cases Count:', approvedTestCasesCount);
  aiLogger.debug('Pending Test Cases Count:', pendingTestCasesCount);
  aiLogger.debug('Coverage Percent:', coveragePercent);
  
  // Render tab content based on activeTab
  const renderTabContent = () => {
    switch (activeTab) {
             case 'overview':
         return (
          <ModernDashboard
             projectSummary={projectSummary}
             onStartExploration={handleStartExploration}
             onGenerateAutomation={handleGenerateAutomation}
             onRunAutomation={handleRunAutomation}
             explorationStatus={explorationStatus}
             automationResults={automationResults}
             onRefreshData={async () => {
               await fetchProjectSummary();
               await fetchTestCases();
             }}
            onOpenQuality={() => handleTabChange('quality')}
           />
         );
      
      case 'ui-explorer':
        return (
          <div className="card">
            <h3 style={{color: '#2e2e2e', marginBottom: '1rem'}}>UI Explorer</h3>
            <p style={{color: '#6b7280', lineHeight: '1.6'}}>UI Explorer is disabled in current phase.</p>
          </div>
        );
      
      case 'test-cases':
        return (
          <TestCasesManager 
            selectedProject={selectedProject}
            authenticatedUser={authenticatedUser}
            apiClient={apiClient}
          />
        );
      
      case 'automation-test-cases':
        // Legacy automation module stays disabled (Phase boundary).
        return (
          <div className="card">
            <h3 style={{color: '#2e2e2e', marginBottom: '1rem'}}>Automation</h3>
            <p style={{color: '#6b7280', lineHeight: '1.6'}}>Automation is disabled in current phase.</p>
          </div>
        );

      case 'automation':
        return (
          <AutomationCases
            selectedProject={selectedProject}
            apiClient={apiClient}
            onNavigateToSettings={onNavigateToSettings}
          />
        );
      
      case 'chat':
        return (
          <QueryChat selectedProject={selectedProject} authenticatedUser={authenticatedUser} />
        );
      
      case 'reports':
        return (
          <div className="card">
            <h3 style={{color: '#2e2e2e', marginBottom: '1rem'}}>📊 Reports</h3>
            <p style={{color: '#6b7280', lineHeight: '1.6'}}>
              View detailed reports and analytics for your test execution and coverage.
            </p>
          </div>
        );
      
      case 'settings':
        return (
          <div className="card">
            <h3 style={{color: '#2e2e2e', marginBottom: '1rem'}}>⚙️ Settings</h3>
            <p style={{color: '#6b7280', lineHeight: '1.6'}}>
              Configure project settings, integrations, and preferences.
            </p>
          </div>
        );
      case 'product-knowledge':
        return (
        <ProductKnowledge selectedProject={selectedProject} />
        );

      case 'health-insights':
        return (
          <HealthInsights
            selectedProject={selectedProject}
            authenticatedUser={authenticatedUser}
            apiClient={apiClient}
          />
        );

      case 'safe-summary-eligibility':
        return (
          <SafeSummaryEligibilityAdmin
            selectedProject={selectedProject}
            authenticatedUser={authenticatedUser}
            apiClient={apiClient}
          />
        );

      case 'repository-source-config':
        return (
          <RepositorySourceConfigAdmin
            selectedProject={selectedProject}
            authenticatedUser={authenticatedUser}
            apiClient={apiClient}
          />
        );

      case 'quality':
        return (
          <QualityDashboard projectId={selectedProject?.project_id} />
        );
      
      default:
        return null;
    }
  };

  // Show full-screen error page if there's a UI Explorer error
  if (uiExplorerError && activeTab === 'ui-explorer') {
    return (
      <ErrorPage
        error={uiExplorerError}
        onRetry={() => {
          setUIExplorerError(null);
          // Retry by refreshing the page or restarting exploration
          window.location.reload();
        }}
        onGoHome={() => {
          setUIExplorerError(null);
          handleTabChange('overview');
        }}
      />
    );
  }

  return (
    <div className="dashboard-container">
      <ModernNavigation
        authenticatedUser={authenticatedUser}
        selectedProject={selectedProject}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onLogout={onLogout}
        onNavigateToUserManager={onNavigateToUserManager}
        onNavigateToSettings={onNavigateToSettings}
        onOpenChangePassword={onOpenChangePassword}
        onProjectSwitch={onProjectSwitch}
        onProjectReconcile={onProjectReconcile}
        apiClient={apiClient}
        projectSummary={projectSummary}
      />
      <main className="main-content" style={{ marginLeft: '280px', marginTop: '64px' }}>
        <CostBreakerBanner selectedProject={selectedProject} />
        {renderTabContent()}
      </main>
    </div>
  );
};

export default Dashboard; 